/**
 * Airwallex Radar Types
 * Type definitions for Airwallex fraud detection (device fingerprint) handling
 */

/**
 * The shape of requireActionData when the server requires an Airwallex Radar session.
 * Received when a payment is attempted without a prior device fingerprint.
 */
export interface AirwallexRadarActionData {
    type: 'radar';
    metadata: {
        provider: 'airwallex';
        isTest: boolean;
    };
}

/**
 * API client interface — consumers inject their own HTTP implementation.
 * Mirrors IThreedsApiClient in the threeds module.
 */
export interface IAirwallexRadarApiClient {
    /**
     * Persist the Airwallex radar session ID to the backend.
     * Maps to POST /api/public/v1/radar-sessions (API key auth)
     */
    saveRadarSession(data: {
        paymentId: string;
        airwallexRadarSessionId: string;
    }): Promise<{ success: boolean; radarSessionId: string }>;

    /**
     * Resume the paused payment after the radar session has been saved.
     * Maps to POST /api/public/v1/payments/continue (API key auth)
     */
    completePaymentAfterAction(paymentId: string): Promise<{
        id?: string;
        status: string;
        requireAction?: string;
        requireActionData?: unknown;
        [key: string]: unknown;
    }>;
}

/**
 * Configuration for AirwallexRadarManager
 */
export interface AirwallexRadarManagerConfig {
    /**
     * Optional API client for saving the session and resuming the payment.
     * Required when calling handleRadarAction(); optional if only calling
     * collectDeviceFingerprint() directly.
     */
    apiClient?: IAirwallexRadarApiClient;
}

/**
 * Result of a completed Airwallex Radar session.
 */
export interface AirwallexRadarSession {
    /** UUID passed as data-order-session-id to the Airwallex fraud script */
    sessionId: string;
    /** The radar_sessions.id returned from the backend, if apiClient was provided */
    savedSessionId?: string;
}

/**
 * Parameters for handleRadarAction()
 */
export interface AirwallexRadarActionParams {
    /** The ID of the paused payment to resume */
    paymentId: string;
    /** Whether to use the Airwallex demo/test environment */
    isTest: boolean;
}

/**
 * Error codes for AirwallexRadarError
 */
export enum AirwallexRadarErrorCode {
    SCRIPT_LOAD_FAILED = 'SCRIPT_LOAD_FAILED',
    SESSION_SAVE_FAILED = 'SESSION_SAVE_FAILED',
    RESUME_FAILED = 'RESUME_FAILED',
    NO_API_CLIENT = 'NO_API_CLIENT',
    SSR_NOT_SUPPORTED = 'SSR_NOT_SUPPORTED',
}

/**
 * Error thrown by AirwallexRadarManager
 */
export class AirwallexRadarError extends Error {
    constructor(
        public code: AirwallexRadarErrorCode,
        message: string,
        public details?: unknown,
    ) {
        super(message);
        this.name = 'AirwallexRadarError';
    }
}
